import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate, Navigate, Link } from "react-router-dom";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import { useAuth } from "../../context/AuthContext";

import {
  previewQuote,
  placeOrder,
  getMyWallet,
  getNearbyRiders,
  setPreferredRider,
  getMyProfile,
  getSavedContacts,
  addSavedContact,
  uploadFile,
  getLaunchedCities,
  reverseGeocodeServer,
} from "../../api/client";

import AddressAutocomplete from "../../components/AddressAutocomplete";
import { loadGoogleMaps } from "../../utils/loadGoogleMaps";
import { detectNigerianState } from "../../utils/detectNigerianState";

const VEHICLE_OPTIONS = [
  { value: "bike", label: "Bike", icon: "🏍️", desc: "Fastest for small items — small/nearby packages may be matched to a bicycle dispatcher" },
  { value: "van", label: "Van", icon: "🚐", desc: "Bigger loads, multiple items" },
  { value: "truck", label: "Truck", icon: "🚚", desc: "Heavy / industrial — quoted on request" },
];

// Each vehicle has its own realistic weight/size tiers — a bike's "large"
// isn't remotely the same load as a van's or a truck's.
const PACKAGE_TYPES_BY_VEHICLE = {
  bike: [
    { value: "bike_under_5kg", label: "Document / Small Parcel (under 5kg)" },
    { value: "bike_5_10kg", label: "Medium Parcel (5–10kg)" },
    { value: "bike_10_20kg", label: "Large Parcel (10–20kg)" },
  ],
  van: [
    { value: "van_under_50kg", label: "Small Load (under 50kg)" },
    { value: "van_50_200kg", label: "Medium Load (50–200kg)" },
    { value: "van_200_500kg", label: "Large Load (200–500kg)" },
  ],
  truck: [
    { value: "truck_under_1t", label: "Light Freight (under 1 ton)" },
    { value: "truck_1_3t", label: "Medium Freight (1–3 tons)" },
    { value: "truck_3plus_t", label: "Heavy Freight (3+ tons)" },
  ],
};

const PACKAGE_CATEGORIES = [
  { value: "food_consumables", label: "Food / Consumables" },
  { value: "clothing_accessories", label: "Clothing / Accessories" },
  { value: "chemical_liquid", label: "Chemical / Liquid" },
  { value: "equipment_tool", label: "Equipment / Tool" },
  { value: "home_appliances", label: "Home Appliances" },
  { value: "mobile_gadgets", label: "Mobile Gadgets" },
  { value: "medical", label: "Medical" },
  { value: "essential_supplies", label: "Essential Supplies" },
  { value: "others", label: "Others (please describe)" },
];

const RA_METHODS = [
  { value: "pin", label: "PIN — receiver reads a code from their phone" },
  { value: "signature", label: "Name & Signature" },
  { value: "photo", label: "Photo of Receiver" },
  { value: "id_photo", label: "Photo of Receiver's ID Card" },
];

const EMPTY_POINT = { placeId: null, address: "", lat: null, lng: null };

const isValidPoint = (point) => (
  point &&
  typeof point.lat === "number" &&
  typeof point.lng === "number" &&
  Number.isFinite(point.lat) &&
  Number.isFinite(point.lng) &&
  point.lat >= -90 && point.lat <= 90 &&
  point.lng >= -180 && point.lng <= 180
);

const reverseGeocode = async (lat, lng) => {
  // 1. Try the browser's client-side Google Geocoder first.
  try {
    const google = await loadGoogleMaps();
    if (google?.maps?.Geocoder) {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });
      if (response.results && response.results[0]) {
        return {
          address: response.results[0].formatted_address,
          placeId: response.results[0].place_id,
        };
      }
    }
  } catch (err) {
    console.warn("Client-side reverse geocoding failed, trying server fallback:", err);
  }

  // 2. Fall back to the backend's server-side geocoder (independent API key/quota),
  // so a real street address still shows up for both the sender and the rider.
  try {
    const { data } = await reverseGeocodeServer(lat, lng);
    if (data?.address) {
      return { address: data.address, placeId: data.placeId || "server_geocoded" };
    }
  } catch (err) {
    console.warn("Server-side reverse geocoding failed:", err);
  }

  // 3. Last resort — raw coordinates, only if both geocoders are unavailable.
  return {
    address: `Current Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`,
    placeId: "current_location",
  };
};

export default function PlaceOrder() {
  const navigate = useNavigate();

  // Core location state
  const [pickup, setPickup] = useState(EMPTY_POINT);
  const [dropoff, setDropoff] = useState(EMPTY_POINT);
  const [pickupKey, setPickupKey] = useState(0);
  const [dropoffKey, setDropoffKey] = useState(0);
  const [stops, setStops] = useState([]); // multi-stop
  const [stopKeys, setStopKeys] = useState([]);
  const [stopContacts, setStopContacts] = useState([]); // { name, phone } per stop
  const [stopInstructions, setStopInstructions] = useState([]); // free-text brief per stop

  // Geolocation loading
  const [locatingPickup, setLocatingPickup] = useState(false);
  const [locatingDropoff, setLocatingDropoff] = useState(false);

  // Order options
  const [vehicleType, setVehicleType] = useState("bike");
  const [packageType, setPackageType] = useState("bike_under_5kg");
  const [packageCategory, setPackageCategory] = useState("others");
  const [packageDescription, setPackageDescription] = useState("");
  const [packagePhotoUrl, setPackagePhotoUrl] = useState(null);
  const [packagePhotoUploading, setPackagePhotoUploading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("paystack");

  // Receiver Acknowledgement
  const [raEnabled, setRaEnabled] = useState(false);
  const [raMethod, setRaMethod] = useState("pin");

  // Collect-on-behalf-of-sender — inactive globally until gateway modalities finalized
  const [collectOnBehalf, setCollectOnBehalf] = useState(false);

  // Scheduling
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");

  // Preferred rider
  const [nearbyRiders, setNearbyRiders] = useState([]);
  const [selectedRiderId, setSelectedRiderId] = useState(null);
  const [currentPreferredRiderId, setCurrentPreferredRiderId] = useState(null);
  const [showRiderPicker, setShowRiderPicker] = useState(false);
  const [ridersLoading, setRidersLoading] = useState(false);

  // Wallet
  const [walletBalance, setWalletBalance] = useState(null);

  // UI state
  const [quote, setQuote] = useState(null);
  // Truck is quoted on request, not priced/charged at booking — a
  // dedicated "received" state replaces the normal price breakdown +
  // navigate-to-track flow for it.
  const [truckSubmitted, setTruckSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Phase 3 state
  const [city, setCity] = useState("Lagos");
  const [launchedCities, setLaunchedCities] = useState(["Lagos", "Abuja", "Port Harcourt"]);
  const [membershipTier, setMembershipTier] = useState("standard");
  const [isInsured, setIsInsured] = useState(false);
  const [declaredValue, setDeclaredValue] = useState("");

  // Contact Persons state
  const [pickupContact, setPickupContact] = useState({ name: "", phone: "" });
  const [dropoffContact, setDropoffContact] = useState({ name: "", phone: "" });
  const [savedContacts, setSavedContacts] = useState([]);
  const [savingContactType, setSavingContactType] = useState(null); // "pickup" | "dropoff" | null

  // Load wallet balance, profile, membership tier & saved contacts on mount
  useEffect(() => {
    getMyWallet()
      .then(r => setWalletBalance(r.data.balance))
      .catch(() => {});
    getMyProfile()
      .then(r => {
        if (r.data.membershipTier) setMembershipTier(r.data.membershipTier);
        if (r.data.preferredRiderId) {
          setCurrentPreferredRiderId(r.data.preferredRiderId._id || r.data.preferredRiderId);
          setSelectedRiderId(r.data.preferredRiderId._id || r.data.preferredRiderId);
        }
      })
      .catch(() => {});
    getSavedContacts()
      .then(r => setSavedContacts(r.data))
      .catch(() => {});
    getLaunchedCities()
      .then(r => { if (r.data.launchedCities?.length) setLaunchedCities(r.data.launchedCities); })
      .catch(() => {});
  }, []);

  // Load nearby riders when pickup is resolved
  useEffect(() => {
    if (!isValidPoint(pickup) || !showRiderPicker) return;
    setRidersLoading(true);
    getNearbyRiders(pickup.lat, pickup.lng)
      .then(r => setNearbyRiders(r.data))
      .catch(() => setNearbyRiders([]))
      .finally(() => setRidersLoading(false));
  }, [pickup, showRiderPicker]);

  const handleUseCurrentLocation = async (type) => {
    setError("");
    const isPickup = type === "pickup";
    if (isPickup) setLocatingPickup(true);
    else setLocatingDropoff(true);

    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      if (isPickup) setLocatingPickup(false);
      else setLocatingDropoff(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const { address, placeId } = await reverseGeocode(lat, lng);
          const point = { placeId, address, lat, lng };
          if (isPickup) {
            setPickup(point);
            setPickupKey(k => k + 1);
          } else {
            setDropoff(point);
            setDropoffKey(k => k + 1);
          }
          setQuote(null);
        } catch (err) {
          setError("Failed to resolve location address.");
        } finally {
          if (isPickup) setLocatingPickup(false);
          else setLocatingDropoff(false);
        }
      },
      (err) => {
        let msg = "Could not retrieve your current location.";
        if (err.code === 1) msg = "Location permission denied. Please enable location access in your browser.";
        else if (err.code === 2) msg = "Current location is unavailable.";
        else if (err.code === 3) msg = "Location request timed out.";
        setError(msg);
        if (isPickup) setLocatingPickup(false);
        else setLocatingDropoff(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Multi-stop management
  const addStop = () => {
    setStops(s => [...s, EMPTY_POINT]);
    setStopKeys(k => [...k, Date.now()]);
    setStopContacts(c => [...c, { name: "", phone: "" }]);
    setStopInstructions(ins => [...ins, ""]);
    setQuote(null);
  };

  const removeStop = (idx) => {
    setStops(s => s.filter((_, i) => i !== idx));
    setStopKeys(k => k.filter((_, i) => i !== idx));
    setStopContacts(c => c.filter((_, i) => i !== idx));
    setStopInstructions(ins => ins.filter((_, i) => i !== idx));
    setQuote(null);
  };

  const handleStopSelect = (idx, point) => {
    setStops(s => s.map((st, i) => i === idx ? point : st));
    setQuote(null);
  };

  const handleStopContactChange = (idx, field, value) => {
    setStopContacts(c => c.map((sc, i) => i === idx ? { ...sc, [field]: value } : sc));
  };

  const handleStopInstructionsChange = (idx, value) => {
    setStopInstructions(ins => ins.map((v, i) => i === idx ? value : v));
  };

  const coordsReady = useMemo(() =>
    isValidPoint(pickup) && isValidPoint(dropoff) && stops.every(isValidPoint),
    [pickup, dropoff, stops]
  );

  const handlePickupSelect = useCallback((point) => { setPickup(point); setQuote(null); setError(""); }, []);
  const handleDropoffSelect = useCallback((point) => { setDropoff(point); setQuote(null); setError(""); }, []);

  const handlePackagePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPackagePhotoUploading(true);
    setError("");
    try {
      const { data } = await uploadFile(file, "packages");
      setPackagePhotoUrl(data.url);
    } catch (err) {
      setError(err.response?.data?.error || "Could not upload package photo.");
    } finally {
      setPackagePhotoUploading(false);
    }
  };

  // Auto-detect Nigerian states from the Google Places address strings.
  // These are passed to the backend's resolveIsInterstate — no manual input needed.
  const pickupState = useMemo(() => detectNigerianState(pickup?.address), [pickup?.address]);
  const dropoffState = useMemo(() => detectNigerianState(dropoff?.address), [dropoff?.address]);
  const isInterstateDetected = !!(pickupState && dropoffState && pickupState !== dropoffState);

  const toPayload = useCallback(() => ({
    pickup: { placeId: pickup.placeId, address: pickup.address, lat: pickup.lat, lng: pickup.lng },
    dropoff: { placeId: dropoff.placeId, address: dropoff.address, lat: dropoff.lat, lng: dropoff.lng },
    pickupContact: pickupContact.name ? pickupContact : undefined,
    dropoffContact: dropoffContact.name ? dropoffContact : undefined,
    stops: stops.map((s, i) => ({
      address: s.address,
      lat: s.lat,
      lng: s.lng,
      contact: stopContacts[i]?.name ? stopContacts[i] : undefined,
      instructions: stopInstructions[i] || undefined,
    })),
    vehicleType,
    packageType,
    packageCategory,
    packageDescription,
    packagePhotoUrl: packagePhotoUrl || undefined,
    city,
    pickupState: pickupState || undefined,
    dropoffState: dropoffState || undefined,
    isInsured,
    declaredValue: Number(declaredValue) || 0,
    receiverAcknowledgement: { enabled: raEnabled, method: raMethod },
    collectOnBehalf,
    scheduledFor: isScheduled && scheduledFor ? scheduledFor : undefined,
    preferredRiderId: selectedRiderId || undefined,
  }), [pickup, dropoff, pickupContact, dropoffContact, stops, stopContacts, stopInstructions, vehicleType, packageType, packageCategory, packageDescription, packagePhotoUrl, city, pickupState, dropoffState, isInsured, declaredValue, raEnabled, raMethod, collectOnBehalf, isScheduled, scheduledFor, selectedRiderId]);

  const handleGetQuote = async () => {
    setError("");
    if (!isValidPoint(pickup)) return setError("Please select a valid pickup location.");
    if (!isValidPoint(dropoff)) return setError("Please select a valid drop-off location.");
    if (stops.some(s => !isValidPoint(s))) return setError("One or more stops have invalid locations.");
    if (isScheduled && !scheduledFor) return setError("Please select a date and time for the scheduled delivery.");
    setLoading(true);
    try {
      const { data } = await previewQuote({ ...toPayload(), paymentMethod });
      setQuote(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not get a quote.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setError("");
    if (!quote) return setError("Please calculate a delivery quote first.");
    if (!isValidPoint(pickup)) return setError("Pickup location is invalid.");
    if (!isValidPoint(dropoff)) return setError("Drop-off location is invalid.");
    setLoading(true);
    try {
      // Truck is agent-quoted, not priced/charged at booking — "cash" is
      // just a neutral placeholder so the order still satisfies the
      // required paymentMethod field; no payment is initiated for it.
      const effectivePaymentMethod = vehicleType === "truck" ? "cash" : paymentMethod;
      const { data } = await placeOrder({ ...toPayload(), paymentMethod: effectivePaymentMethod });

      if (vehicleType === "truck") {
        setTruckSubmitted(true);
        return;
      }

      // Digital-gateway orders need an actual checkout before the delivery is
      // confirmed — redirect there now. The gateway bounces back to
      // /payment-callback, which verifies and lands on the track page.
      if (data.paymentInit?.checkoutUrl && !data.paymentInit.isMocked) {
        window.location.href = data.paymentInit.checkoutUrl;
        return;
      }
      navigate(`/track/${data._id}`);
    } catch (err) {
      setError(err.response?.data?.error || "Could not place order.");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferredRider = async (riderId) => {
    try {
      await setPreferredRider(riderId);
      setCurrentPreferredRiderId(riderId);
      setSelectedRiderId(riderId);
    } catch {
      setError("Could not save preferred dispatcher.");
    }
  };

  const handleSaveContactToFavorites = async (type) => {
    const contact = type === "pickup" ? pickupContact : dropoffContact;
    if (!contact.name || !contact.phone) {
      setError(`Please enter a name and phone number for ${type} contact first.`);
      return;
    }
    setSavingContactType(type);
    try {
      const { data } = await addSavedContact({
        name: contact.name,
        phone: contact.phone,
        label: `${contact.name} (${type === "pickup" ? "Pickup" : "Drop-off"})`
      });
      setSavedContacts(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save contact to favorites.");
    } finally {
      setSavingContactType(null);
    }
  };

  // Min datetime: 5 minutes from now
  const minDateTime = new Date(Date.now() + 5 * 60 * 1000)
    .toISOString().slice(0, 16);

  const { account } = useAuth();

  // Corporate accounts book exclusively through their own dashboard (one-off,
  // scheduled, or recurring standing orders) — this generic order page isn't
  // part of their flow, so redirect rather than showing it.
  if (account?.corporateApprovalStatus === "approved") {
    return <Navigate to="/b2b" replace />;
  }

  if (truckSubmitted) {
    return (
      <div className="user-app">
        <BottomNav />
        <div className="user-app-main">
          <UserPageHeader walletBalance={walletBalance} membershipTier={membershipTier} />
          <div className="page" style={{ textAlign: "center" }}>
            <span style={{ fontSize: "48px", display: "block", marginBottom: "12px" }}>📦</span>
            <h1>Your order has been received</h1>
            <p className="hint" style={{ marginTop: "8px", fontSize: "15px" }}>
              Our agents will contact you shortly to confirm pricing and schedule your truck dispatch.
            </p>
            <div className="row" style={{ marginTop: "20px" }}>
              <button className="secondary" onClick={() => navigate("/orders")}>View My Orders</button>
              <button onClick={() => { setTruckSubmitted(false); setQuote(null); }}>Place Another Order</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader
          walletBalance={walletBalance}
          membershipTier={membershipTier}
        />

      {/* ── Welcome Banner ── */}
      <div className="welcome-banner">
        <h2>Hello, {account?.name?.split(" ")[0] || "there"} 👋</h2>
        <p>Where should we deliver today?</p>
      </div>

      {/* ── Quick-Access Cards ── */}
      {!quote && (
        <div className="quick-cards">
          <Link to="/wallet" className="quick-card">
            <span className="quick-card-icon">💰</span>
            <span className="quick-card-title">Wallet</span>
            <span className="quick-card-desc">Top up &amp; pay for deliveries</span>
          </Link>
          <Link to="/subscription" className="quick-card">
            <span className="quick-card-icon">⭐</span>
            <span className="quick-card-title">Subscription</span>
            <span className="quick-card-desc">FASTSEND Plus &amp; Saver</span>
          </Link>
          <Link to="/business" className="quick-card">
            <span className="quick-card-icon">🏢</span>
            <span className="quick-card-title">Business</span>
            <span className="quick-card-desc">Agent, Fleet, B2B &amp; Corporate</span>
          </Link>
          <Link to="/track/last" className="quick-card" onClick={e => e.preventDefault()} style={{ opacity: 0.5, cursor: "default" }}>
            <span className="quick-card-icon">📍</span>
            <span className="quick-card-title">Track</span>
            <span className="quick-card-desc">Follow an active delivery</span>
          </Link>
        </div>
      )}

      <div className="page page-with-nav" style={{ marginTop: "16px" }}>
        <div className="brand-header" style={{ marginBottom: "16px" }}>
          <span className="brand-name">Send a Package</span>
          <p className="brand-tagline">Calculate fare and book a dispatcher</p>
        </div>

        <div className="step-indicator">
          <div className={`step-dot ${!quote ? "active" : "completed"}`} />
          <div className={`step-dot ${quote ? "active" : ""}`} />
        </div>

      {!quote ? (
        <>
          {/* VEHICLE TYPE */}
          <div className="form-field">
            <label>Vehicle Type</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
              {VEHICLE_OPTIONS.map(v => (
                <button
                  key={v.value}
                  type="button"
                  className={vehicleType === v.value ? "" : "secondary"}
                  style={{ padding: "10px 4px", fontSize: "13px", marginTop: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}
                  onClick={() => { setVehicleType(v.value); setPackageType(PACKAGE_TYPES_BY_VEHICLE[v.value][0].value); setQuote(null); }}
                >
                  <span style={{ fontSize: "18px" }}>{v.icon}</span>
                  {v.label}
                </button>
              ))}
            </div>
            <p className="hint">{VEHICLE_OPTIONS.find(v => v.value === vehicleType)?.desc}</p>
            {vehicleType === "truck" && (
              <p className="hint" style={{ color: "var(--warning)" }}>
                Truck deliveries are quoted and dispatched on-demand — our team will confirm availability after you request.
              </p>
            )}
          </div>

          {/* PICKUP */}
          <div className="form-field">
            <div className="label-with-action">
              <label>Pickup Location</label>
              <button type="button" className="location-btn"
                onClick={() => handleUseCurrentLocation("pickup")}
                disabled={locatingPickup || loading}>
                {locatingPickup ? "📍 Detecting..." : "📍 Use Current Location"}
              </button>
            </div>
            <AddressAutocomplete key={pickupKey} placeholder="Search pickup address..." onSelect={handlePickupSelect} />
            {pickup.address && (
              <div className="selected-location">
                <strong>Selected:</strong> {pickup.address}
                {isValidPoint(pickup) && <small>Coordinates: {pickup.lat.toFixed(6)}, {pickup.lng.toFixed(6)}</small>}
              </div>
            )}

            {/* PICKUP CONTACT PERSON */}
            <div className="form-field" style={{ background: "rgba(255,255,255,0.03)", padding: "12px", borderRadius: "8px", marginTop: "8px", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)" }}>
                  👤 Pickup Contact Person (Optional)
                </label>
                {account && (
                  <button
                    type="button"
                    className="location-btn"
                    onClick={() => setPickupContact({ name: account.name || "", phone: account.phone || "" })}
                  >
                    👤 Myself
                  </button>
                )}
              </div>

              {savedContacts.length > 0 && (
                <select
                  style={{ marginBottom: "8px", fontSize: "12px", padding: "6px 8px" }}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const selected = savedContacts.find(c => c._id === e.target.value);
                    if (selected) setPickupContact({ name: selected.name, phone: selected.phone });
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>⭐ Select from Saved Favorites...</option>
                  {savedContacts.map(c => (
                    <option key={c._id} value={c._id}>{c.label || c.name} ({c.name} - {c.phone})</option>
                  ))}
                </select>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <input
                  type="text"
                  placeholder="Contact Name"
                  value={pickupContact.name}
                  onChange={(e) => setPickupContact(prev => ({ ...prev, name: e.target.value }))}
                  style={{ margin: 0, fontSize: "13px" }}
                />
                <input
                  type="tel"
                  placeholder="Contact Phone"
                  value={pickupContact.phone}
                  onChange={(e) => setPickupContact(prev => ({ ...prev, phone: e.target.value }))}
                  style={{ margin: 0, fontSize: "13px" }}
                />
              </div>

              {pickupContact.name && pickupContact.phone && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px" }}>
                  <button
                    type="button"
                    className="location-btn"
                    disabled={savingContactType === "pickup"}
                    onClick={() => handleSaveContactToFavorites("pickup")}
                  >
                    {savingContactType === "pickup" ? "Saving..." : "⭐ Save to Favorites"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* MULTI-STOPS */}
          {stops.map((stop, idx) => (
            <div className="form-field" key={stopKeys[idx]}>
              <div className="label-with-action">
                <label>Stop {idx + 1}</label>
                <button type="button" className="location-btn" style={{ background: "rgba(239,68,68,0.1)", color: "var(--danger-hover)", borderColor: "rgba(239,68,68,0.3)" }}
                  onClick={() => removeStop(idx)}>
                  ✕ Remove
                </button>
              </div>
              <AddressAutocomplete key={stopKeys[idx]} placeholder={`Stop ${idx + 1} address...`} onSelect={p => handleStopSelect(idx, p)} />
              {isValidPoint(stop) && (
                <div className="selected-location">
                  <strong>Selected:</strong> {stop.address}
                  <small>Coordinates: {stop.lat.toFixed(6)}, {stop.lng.toFixed(6)}</small>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "8px" }}>
                <input
                  type="text"
                  placeholder="Contact Name (Optional)"
                  value={stopContacts[idx]?.name || ""}
                  onChange={(e) => handleStopContactChange(idx, "name", e.target.value)}
                  style={{ margin: 0, fontSize: "13px" }}
                />
                <input
                  type="tel"
                  placeholder="Contact Phone (Optional)"
                  value={stopContacts[idx]?.phone || ""}
                  onChange={(e) => handleStopContactChange(idx, "phone", e.target.value)}
                  style={{ margin: 0, fontSize: "13px" }}
                />
              </div>
              <textarea
                placeholder="Instructions/brief for this stop (Optional) — e.g. gate code, who to ask for, what to drop off"
                value={stopInstructions[idx] || ""}
                onChange={(e) => handleStopInstructionsChange(idx, e.target.value)}
                rows={2}
                style={{ marginTop: "8px", marginBottom: 0, fontSize: "13px", resize: "vertical" }}
              />
            </div>
          ))}

          <button type="button" className="secondary" style={{ marginBottom: "8px" }} onClick={addStop}>
            + Add Stop
          </button>

          {/* DROPOFF */}
          <div className="form-field">
            <div className="label-with-action">
              <label>Drop-off Location</label>
              <button type="button" className="location-btn"
                onClick={() => handleUseCurrentLocation("dropoff")}
                disabled={locatingDropoff || loading}>
                {locatingDropoff ? "📍 Detecting..." : "📍 Use Current Location"}
              </button>
            </div>
            <AddressAutocomplete key={dropoffKey} placeholder="Search destination address..." onSelect={handleDropoffSelect} />
            {dropoff.address && (
              <div className="selected-location">
                <strong>Selected:</strong> {dropoff.address}
                {isValidPoint(dropoff) && <small>Coordinates: {dropoff.lat.toFixed(6)}, {dropoff.lng.toFixed(6)}</small>}
              </div>
            )}

            {/* DROPOFF CONTACT PERSON */}
            <div className="form-field" style={{ background: "rgba(255,255,255,0.03)", padding: "12px", borderRadius: "8px", marginTop: "8px", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)" }}>
                  👤 Drop-off Contact Person (Optional)
                </label>
                {account && (
                  <button
                    type="button"
                    className="location-btn"
                    onClick={() => setDropoffContact({ name: account.name || "", phone: account.phone || "" })}
                  >
                    👤 Myself
                  </button>
                )}
              </div>

              {savedContacts.length > 0 && (
                <select
                  style={{ marginBottom: "8px", fontSize: "12px", padding: "6px 8px" }}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const selected = savedContacts.find(c => c._id === e.target.value);
                    if (selected) setDropoffContact({ name: selected.name, phone: selected.phone });
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>⭐ Select from Saved Favorites...</option>
                  {savedContacts.map(c => (
                    <option key={c._id} value={c._id}>{c.label || c.name} ({c.name} - {c.phone})</option>
                  ))}
                </select>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <input
                  type="text"
                  placeholder="Contact Name"
                  value={dropoffContact.name}
                  onChange={(e) => setDropoffContact(prev => ({ ...prev, name: e.target.value }))}
                  style={{ margin: 0, fontSize: "13px" }}
                />
                <input
                  type="tel"
                  placeholder="Contact Phone"
                  value={dropoffContact.phone}
                  onChange={(e) => setDropoffContact(prev => ({ ...prev, phone: e.target.value }))}
                  style={{ margin: 0, fontSize: "13px" }}
                />
              </div>

              {dropoffContact.name && dropoffContact.phone && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px" }}>
                  <button
                    type="button"
                    className="location-btn"
                    disabled={savingContactType === "dropoff"}
                    onClick={() => handleSaveContactToFavorites("dropoff")}
                  >
                    {savingContactType === "dropoff" ? "Saving..." : "⭐ Save to Favorites"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* CITY SELECTOR */}
          <div className="form-field">
            <label>City</label>
            <select value={city} onChange={e => { setCity(e.target.value); setQuote(null); }}>
              {launchedCities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="hint">More cities coming online as dispatchers are onboarded.</p>
          </div>

          {/* AUTO-DETECTED STATE BADGES — derived from the Google Places address.
              No user input required; the backend receives pickupState / dropoffState
              and applies the interstate surcharge automatically when they differ. */}
          {(pickup?.address || dropoff?.address) && (
            <div className="form-field">
              <label style={{ marginBottom: "8px", display: "block" }}>Detected States</label>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                {pickup?.address && (
                  <span style={{
                    padding: "4px 12px", borderRadius: "20px", fontSize: "13px", fontWeight: 600,
                    background: "rgba(var(--accent-rgb, 99,102,241), 0.12)",
                    color: "var(--text-accent, #6366f1)", border: "1px solid rgba(99,102,241,0.25)"
                  }}>
                    📍 Pickup: {pickupState || "Unknown"}
                  </span>
                )}
                {dropoff?.address && (
                  <span style={{
                    padding: "4px 12px", borderRadius: "20px", fontSize: "13px", fontWeight: 600,
                    background: "rgba(var(--accent-rgb, 99,102,241), 0.12)",
                    color: "var(--text-accent, #6366f1)", border: "1px solid rgba(99,102,241,0.25)"
                  }}>
                    🏁 Drop-off: {dropoffState || "Unknown"}
                  </span>
                )}
              </div>
              {isInterstateDetected && (
                <p className="hint" style={{ color: "var(--warning, #f59e0b)", marginTop: "8px" }}>
                  ⚠️ Interstate delivery detected ({pickupState} → {dropoffState}) — a surcharge of ₦100/km beyond 25km will apply.
                </p>
              )}
            </div>
          )}

          {/* MEMBERSHIP BADGE */}
          {membershipTier === "plus" ? (
            <div className="surge-toggle" style={{ background: "rgba(22, 199, 132, 0.1)", borderColor: "rgba(22, 199, 132, 0.3)" }}>
              <span style={{ color: "var(--success)", fontWeight: 600, fontSize: "14px" }}>
                ⭐ FASTSEND Plus Active — 20% Discount applied!
              </span>
            </div>
          ) : membershipTier === "saver" ? (
            <div className="surge-toggle" style={{ background: "rgba(22, 199, 132, 0.1)", borderColor: "rgba(22, 199, 132, 0.3)" }}>
              <span style={{ color: "var(--success)", fontWeight: 600, fontSize: "14px" }}>
                💚 Saver Plan Active — up to 18% off your next deliveries this month!
              </span>
            </div>
          ) : (
            <div style={{ marginBottom: "12px", fontSize: "13px" }}>
              <a href="/subscription" style={{ color: "var(--text-accent)", textDecoration: "none" }}>
                ⭐ Upgrade to Plus or Saver for discounted delivery fees!
              </a>
            </div>
          )}

          {/* PACKAGE WEIGHT CLASS — tiers are specific to the selected vehicle */}
          <div className="form-field">
            <label>Package Size</label>
            <select value={packageType} onChange={e => { setPackageType(e.target.value); setQuote(null); }}>
              {PACKAGE_TYPES_BY_VEHICLE[vehicleType].map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {packageType === "bike_10_20kg" && vehicleType === "bike" && (
              <p className="hint" style={{ color: "var(--warning)" }}>
                Packages over 10kg are recommended to go by Van. Consider switching vehicle type above.
              </p>
            )}
          </div>

          {vehicleType === "truck" ? (
            /* TRUCK CARGO — freight-scale loads don't fit the standard
               package category field, so this asks what actually matters
               for dispatching a truck instead (size tier is above). */
            <div className="form-field">
              <label>Nature of Cargo</label>
              <textarea
                placeholder="What's being moved? (e.g. building materials, furniture, equipment)"
                value={packageDescription}
                onChange={e => setPackageDescription(e.target.value)}
                rows={2}
                style={{ resize: "vertical" }}
              />
              <div style={{ marginTop: "8px" }}>
                <label className="location-btn" style={{ display: "inline-block", cursor: "pointer" }}>
                  {packagePhotoUploading ? "Uploading..." : packagePhotoUrl ? "📷 Photo attached — change" : "📷 Add Cargo Photo (Optional)"}
                  <input type="file" accept="image/*" onChange={handlePackagePhotoChange} style={{ display: "none" }} disabled={packagePhotoUploading} />
                </label>
              </div>
            </div>
          ) : (
            <>
              {/* PACKAGE CATEGORY */}
              <div className="form-field">
                <label>Package Category</label>
                <select value={packageCategory} onChange={e => { setPackageCategory(e.target.value); setQuote(null); }}>
                  {PACKAGE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <textarea
                  placeholder="Briefly describe what's being sent (optional but recommended)"
                  value={packageDescription}
                  onChange={e => setPackageDescription(e.target.value)}
                  rows={2}
                  style={{ marginTop: "8px", resize: "vertical" }}
                />
                <div style={{ marginTop: "8px" }}>
                  <label className="location-btn" style={{ display: "inline-block", cursor: "pointer" }}>
                    {packagePhotoUploading ? "Uploading..." : packagePhotoUrl ? "📷 Photo attached — change" : "📷 Add Package Photo (Optional)"}
                    <input type="file" accept="image/*" onChange={handlePackagePhotoChange} style={{ display: "none" }} disabled={packagePhotoUploading} />
                  </label>
                </div>
              </div>
            </>
          )}

          {/* RECEIVER ACKNOWLEDGEMENT */}
          <div className="form-field">
            <div className="surge-toggle">
              <div className="surge-label">
                Receiver Acknowledgement (RA)?{" "}
                <span className={raEnabled ? "surge-on" : "surge-off"}>{raEnabled ? "Yes" : "No"}</span>
              </div>
              <button type="button"
                className={`toggle-switch${raEnabled ? " on" : ""}`}
                onClick={() => { setRaEnabled(v => !v); setQuote(null); }}
              />
            </div>
            {raEnabled && (
              <>
                <select value={raMethod} onChange={e => { setRaMethod(e.target.value); setQuote(null); }} style={{ marginTop: "8px" }}>
                  {RA_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <p className="hint">
                  Be sure to tell the receiver how they'll need to verify delivery — a wrong or missed
                  verification method can result in the package being returned (return fees apply).
                </p>
              </>
            )}
          </div>

          {/* COLLECT ON BEHALF — inactive, coming soon */}
          <div className="form-field">
            <div className="surge-toggle" style={{ opacity: 0.5 }}>
              <div className="surge-label">
                Collect Payment on Behalf of Sender?{" "}
                <span className="surge-off">Coming Soon</span>
              </div>
              <button type="button" className="toggle-switch" disabled onClick={() => setCollectOnBehalf(false)} />
            </div>
            <p className="hint">We're finalizing gateway settlement details for this feature — check back soon.</p>
          </div>

          {/* INSURANCE / PACKAGE PROTECTION */}
          <div className="form-field">
            <div className="surge-toggle">
              <div className="surge-label">
                Protect Package (Insurance)?{" "}
                <span className={isInsured ? "surge-on" : "surge-off"}>
                  {isInsured ? "Yes" : "No"}
                </span>
              </div>
              <button type="button"
                className={`toggle-switch${isInsured ? " on" : ""}`}
                onClick={() => { setIsInsured(v => !v); setQuote(null); }}
              />
            </div>
            {isInsured && (
              <input
                type="number"
                placeholder="Declared Package Value (₦)"
                value={declaredValue}
                onChange={e => { setDeclaredValue(e.target.value); setQuote(null); }}
                style={{ marginTop: "8px" }}
              />
            )}
          </div>

          {/* SCHEDULE TOGGLE */}
          <div className="form-field">
            <div className="surge-toggle">
              <div className="surge-label">
                Schedule for later?{" "}
                <span className={isScheduled ? "surge-on" : "surge-off"}>
                  {isScheduled ? "Yes" : "No (Immediate)"}
                </span>
              </div>
              <button type="button"
                className={`toggle-switch${isScheduled ? " on" : ""}`}
                onClick={() => { setIsScheduled(v => !v); setScheduledFor(""); setQuote(null); }}
              />
            </div>
            {isScheduled && (
              <>
                <input type="datetime-local" min={minDateTime} value={scheduledFor}
                  onChange={e => { setScheduledFor(e.target.value); setQuote(null); }}
                  style={{ marginTop: "8px" }} />
                <p className="hint">
                  Scheduled deliveries show a price range — the lower amount applies if there's no surge
                  at dispatch time, the higher amount if surge pricing is active then.
                </p>
              </>
            )}
          </div>

          {/* PREFERRED RIDER */}
          {isValidPoint(pickup) && (
            <div className="form-field">
              <div className="label-with-action">
                <label>Preferred Dispatcher</label>
                <button type="button" className="location-btn"
                  onClick={() => setShowRiderPicker(v => !v)}>
                  {showRiderPicker ? "▲ Hide" : "▼ Choose Dispatcher"}
                </button>
              </div>
              {currentPreferredRiderId && !showRiderPicker && (
                <p className="hint">✓ Preferred dispatcher saved. Will be tried first.</p>
              )}
              {showRiderPicker && (
                <div className="rider-picker">
                  {ridersLoading && <p className="hint">Finding nearby riders...</p>}
                  {!ridersLoading && nearbyRiders.length === 0 && (
                    <p className="hint">No online riders nearby right now.</p>
                  )}
                  {nearbyRiders.map(r => (
                    <div key={r._id} className="rider-card" style={{ cursor: "pointer", borderColor: selectedRiderId === r._id ? "var(--accent)" : undefined }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <strong>{r.name}</strong>
                          <p>🚴 {r.vehicleType} · ⭐ {r.rating?.toFixed(1) || "N/A"}</p>
                          <p style={{ color: r.isOnline ? "var(--text-accent)" : "var(--text-muted)" }}>
                            {r.isOnline ? "🟢 Online" : "⚫ Offline"}
                          </p>
                        </div>
                        <button type="button" style={{ width: "auto", padding: "6px 14px", fontSize: "13px", marginTop: 0 }}
                          className={selectedRiderId === r._id ? "" : "secondary"}
                          onClick={() => {
                            setSelectedRiderId(r._id);
                            handleSavePreferredRider(r._id);
                          }}>
                          {selectedRiderId === r._id ? "✓ Preferred" : "Set as Preferred"}
                        </button>
                      </div>
                    </div>
                  ))}
                  {selectedRiderId && (
                    <button type="button" className="secondary" style={{ marginTop: "8px" }}
                      onClick={() => { setSelectedRiderId(null); setPreferredRider(null); setCurrentPreferredRiderId(null); }}>
                      Clear Preferred Dispatcher
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {error && <p className="error">{error}</p>}

          <button disabled={!coordsReady || loading} onClick={handleGetQuote}>
            {loading ? "Calculating Fare..." : "Get Price Quote"}
          </button>

          {!coordsReady && (
            <p className="hint">Select a pickup and drop-off location from the address suggestions.</p>
          )}
        </>
      ) : (
        <div className="quote-box">
          <h3>Delivery Summary</h3>

          <div className="delivery-card">
            <div className="route-address"><strong>Vehicle:</strong> {VEHICLE_OPTIONS.find(v => v.value === vehicleType)?.icon} {VEHICLE_OPTIONS.find(v => v.value === vehicleType)?.label}</div>
            <div className="route-address" style={{ marginTop: "6px" }}><strong>City:</strong> {city}</div>
            <div className="route-address" style={{ marginTop: "6px" }}><strong>Pickup:</strong> {pickup.address}</div>
            {stops.map((s, i) => (
              <div key={i} className="route-address" style={{ marginTop: "6px" }}>
                <strong>Stop {i + 1}:</strong> {s.address}
              </div>
            ))}
            <div className="route-address" style={{ marginTop: "6px" }}>
              <strong>Drop-off:</strong> {dropoff.address}
            </div>
            {isScheduled && scheduledFor && (
              <div className="route-address" style={{ marginTop: "6px" }}>
                <strong>🕐 Scheduled for:</strong> {new Date(scheduledFor).toLocaleString()}
              </div>
            )}
            {isInsured && (
              <div className="route-address" style={{ marginTop: "6px", color: "var(--text-accent)" }}>
                <strong>🛡️ Insured Value:</strong> ₦{Number(declaredValue).toLocaleString()}
              </div>
            )}
            {raEnabled && (
              <div className="route-address" style={{ marginTop: "6px", color: "var(--text-accent)" }}>
                <strong>✅ Receiver Acknowledgement:</strong> {RA_METHODS.find(m => m.value === raMethod)?.label}
              </div>
            )}
          </div>

          {/* UBER-STYLE VEHICLE COMPARISON */}
          {quote.vehicleOptions && (
            <div className="price-breakdown" style={{ marginTop: "12px" }}>
              <p className="hint" style={{ marginBottom: "6px" }}>Compare vehicle rates for this route:</p>
              {Object.entries(quote.vehicleOptions).map(([vt, q]) => (
                <div key={vt} className="price-row" style={{ fontWeight: vt === vehicleType ? 700 : 400 }}>
                  <span>{VEHICLE_OPTIONS.find(v => v.value === vt)?.icon} {VEHICLE_OPTIONS.find(v => v.value === vt)?.label}{vt === vehicleType ? " (selected)" : ""}</span>
                  <span>₦{q.total?.toLocaleString()}</span>
                </div>
              ))}
              {quote.truckOnDemand && (
                <div className="price-row" style={{ color: "var(--text-muted)" }}>
                  <span>🚚 Truck</span>
                  <span>Quoted on request</span>
                </div>
              )}
            </div>
          )}

          {vehicleType === "truck" ? (
            /* Truck is quoted on request, not priced/charged at booking —
               no computed total, no payment method, no upfront charge. */
            <div className="quote-box" style={{ marginTop: "12px", background: "var(--info-bg)", borderColor: "var(--info-border)" }}>
              <p style={{ fontWeight: 600, color: "var(--info-hover)" }}>🚚 Truck bookings are quoted on request.</p>
              <p className="hint" style={{ marginTop: "4px" }}>
                Submit your request and one of our agents will contact you shortly to confirm pricing and schedule your dispatch — no payment is taken now.
              </p>
            </div>
          ) : (
            <>
              <div className="price-breakdown">
                <div className="price-row">
                  <span>Estimated Distance</span>
                  <span>{quote.distanceKm} km{stops.length > 0 ? " (multi-leg)" : ""}</span>
                </div>
                <div className="price-row"><span>Base Fare</span><span>₦{quote.baseFare}</span></div>
                {quote.surgeMultiplier > 1 && (
                  <div className="price-row"><span>Surge ({quote.surgeMultiplier}×)</span><span>Active</span></div>
                )}
                {quote.interstateCharge > 0 && (
                  <div className="price-row" style={{ color: "var(--warning, #f59e0b)" }}>
                    <span>🛣️ Interstate Surcharge ({quote.interstateKm} km × ₦100)</span>
                    <span>+₦{quote.interstateCharge.toLocaleString()}</span>
                  </div>
                )}
                {quote.discountAmount > 0 && (
                  <div className="price-row" style={{ color: "var(--accent)" }}>
                    <span>Membership Discount</span>
                    <span>-₦{quote.discountAmount}</span>
                  </div>
                )}
                {quote.insuranceFee > 0 && (
                  <div className="price-row">
                    <span>Package Protection Fee</span>
                    <span>+₦{quote.insuranceFee}</span>
                  </div>
                )}
                {quote.raFee > 0 && (
                  <div className="price-row">
                    <span>Receiver Acknowledgement Fee</span>
                    <span>+₦{quote.raFee}</span>
                  </div>
                )}
                {quote.walletDiscountAmount > 0 && (
                  <div className="price-row" style={{ color: "var(--accent)" }}>
                    <span>Wallet Payment Discount</span>
                    <span>-₦{quote.walletDiscountAmount}</span>
                  </div>
                )}
                {isScheduled && quote.lowTotal != null && quote.highTotal != null ? (
                  <div className="price-row total">
                    <span>Estimated Price Range</span>
                    <span>₦{quote.lowTotal.toLocaleString()} – ₦{quote.highTotal.toLocaleString()}</span>
                  </div>
                ) : (
                  <div className="price-row total"><span>Total Price</span><span>₦{quote.total}</span></div>
                )}
              </div>

              <div className="form-field" style={{ marginTop: "16px" }}>
                <label>Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={async (e) => {
                    const method = e.target.value;
                    setPaymentMethod(method);
                    // Re-quote so the wallet payment discount reflects live in the total.
                    try {
                      const { data } = await previewQuote({ ...toPayload(), paymentMethod: method });
                      setQuote(data);
                    } catch {
                      // keep the previously displayed quote if re-quoting fails
                    }
                  }}
                >
                  <option value="paystack">Card / Bank Transfer (Paystack)</option>
                  <option value="flutterwave">Card / Bank Transfer (Flutterwave)</option>
                  {walletBalance !== null && (
                    <option value="wallet" disabled={walletBalance < quote.total}>
                      Wallet Balance — ₦{walletBalance} (5% off!)
                      {walletBalance < quote.total ? " (insufficient)" : ""}
                    </option>
                  )}
                  <option value="cash">Pay on Delivery (Cash)</option>
                  <option value="bank_transfer">Pay on Delivery (Bank Transfer)</option>
                  <option value="receiver_pay">Receiver Pays (via a link you share with them)</option>
                </select>
                {paymentMethod === "receiver_pay" && (
                  <p className="hint" style={{ marginTop: "6px" }}>
                    No payment happens now. After placing the order, create a share link from the tracking
                    page and send it to your receiver — they can pay online there, no account needed.
                  </p>
                )}
              </div>
            </>
          )}

          {error && <p className="error">{error}</p>}

          <div className="row">
            <button className="secondary" onClick={() => setQuote(null)} disabled={loading}>Edit Details</button>
            <button disabled={loading} onClick={handleConfirm}>
              {loading ? "Submitting..." : vehicleType === "truck" ? "Submit Truck Request" : isScheduled ? "Schedule & Request Dispatcher" : "Confirm & Request Dispatcher"}
            </button>
          </div>
        </div>
      )}
      </div>

      </div>
    </div>
  );
}
